import React, { useState, useRef, useEffect, useMemo } from "react";
import cx from "classnames";
import { Map } from "immutable";
import { useDrag, useDrop } from "react-dnd";
import { Icon } from "@showitinc/showit-wc";

import { generateDragSpec, generateDropSpec, getTargetArea } from "../../../utils/DnDUtils";
import AppConstants from "../../../constants/AppConstants";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import BlockUpdateVO from "@/vo/BlockUpdate.mjs";
import UIActions from "../../../actions/UIActions";
import IDUtils from "../../../utils/IDUtils";
import EventUtils from "../../../utils/EventUtils";
import { BOTTOM, CENTER, ELEMENT_LAYER_ITEM } from "../../../constants/DnDConstants";
import { pagePanelDrop } from "../SitePanelDropHandlers";
import slugify from "../../../utils/SlugifyUtils";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import uiPrefix from "../../../utils/UiPrefixUtils";
import DataTypes, { GROUP } from "../../../constants/DataTypes";
import PagePanelElementContextMenu from "../../../app2components/ContextMenus/PagePanelElementContextMenu";
import { DROP_AREA_CENTER, DROP_AREA_TOP, DROP_AREA_BOTTOM } from "@/constants/LayerConstants";
import { getFormFieldDisplayName, getFormFieldMenuItem } from "@/utils/FormFieldUtils";
import styles from "./LayerPanelElement.module.css";

/**
 * @typedef {Object} LayerRowStyle
 * @property {string | null} iconClass - showit-wc icon name, or null for no icon.
 * @property {string} label - The row's display text.
 */

const {
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_CHECKBOX,
  ITEM_TYPE_GROUP,
  ITEM_TYPE_ELEMENT,
  LOCK_EDITING_KEY,
  VISIBLE_ALL,
  VISIBLE_DESKTOP,
  VISIBLE_MOBILE,
  UI_BUTTON_SIZE_SMALL,
  DROP_SHADOW_TOP,
  DROP_SHADOW_BOTTOM,
  DROP_SHADOW_CENTER,
  PAGE_PANEL_STATE_DEFAULT,
  PAGE_PANEL_STATE_DISTANT,
  DROP_CONTAINER_BACKGROUND,
} = AppConstants;

/**
 * Picks the icon and label a layer row shows for an element.
 * @param {import("immutable").Map<string, any>} element
 * @param {boolean} iconHover - Whether the row's icon is hovered; groups swap to a chevron.
 * @returns {LayerRowStyle}
 */
const getElementStyle = (element, iconHover) => {
  const elementType = element.get("type");
  /** @type {LayerRowStyle} */
  const data = {
    iconClass: null,
    label: element.get("name", elementType === ELEMENT_TYPE_SIMPLE ? "shape" : elementType),
  };

  switch (elementType) {
    case ELEMENT_TYPE_GRAPHIC:
      data.iconClass = "mediaLibrary";
      break;
    case ELEMENT_TYPE_SIMPLE:
    case ELEMENT_TYPE_SVG:
      data.iconClass = "roundedSquareElement";
      break;
    case ELEMENT_TYPE_LINE:
      data.iconClass = "lineElement";
      break;
    case ELEMENT_TYPE_HTML_TEXT:
    case ELEMENT_TYPE_IFRAME:
      data.iconClass = "embedElement";
      break;
    case ELEMENT_TYPE_TEXT:
      data.iconClass = "textElement";
      if (elementType === data.label) {
        data.label = element.getIn(["content", "sample"], data.label);
      }
      break;
    case ELEMENT_TYPE_VIDEO:
      data.iconClass = "videoElement";
      break;
    case ELEMENT_TYPE_SOCIAL_GRID:
      data.iconClass = "socialGridElement";
      break;
    case ELEMENT_TYPE_GALLERY:
      data.iconClass = "galleryElement";
      break;
    case ELEMENT_TYPE_ICON:
      data.iconClass = "symbolElement";
      break;
    case ELEMENT_TYPE_BUTTON:
      data.iconClass = "buttonElement";
      break;
    case ELEMENT_TYPE_LOTTIE:
      data.iconClass = "lottieFiles";
      break;
    case ELEMENT_TYPE_FORM_INPUT:
    case ELEMENT_TYPE_FORM_SELECT:
    case ELEMENT_TYPE_FORM_RADIO:
    case ELEMENT_TYPE_FORM_CHECKBOX: {
      data.iconClass = getFormFieldMenuItem(element)?.icon ?? null;
      if (elementType === data.label) data.label = getFormFieldDisplayName(element) ?? data.label;
      break;
    }
    case GROUP:
      data.iconClass = iconHover ? "arrowChevronDown" : "group";
      break;
    default:
      break;
  }
  return data;
};

const LayerPanelElement = ({
  element = Map(),
  elemKey,
  id,
  index,
  layerType,
  locked,
  onSelect,
  onVisibleChange,
  selected,
  theme,
  selectedChildren,
  setSelectedChildren,
  selectedCanvasView,
  setSelectedCanvasView,
  stateChild,
  setIsCanvasViewOpened,
  nonCanvasViewElementSelected,
  group,
  groupId,
  groupPath,
  isGroupOpen,
}) => {
  const [dropArea, setDropArea] = useState(null);
  const [iconHover, setIconHover] = useState(false);
  const selfRef = useRef();
  const updateKey = elemKey;
  const isGroupLayer = element.get("type") === DataTypes.GROUP;

  // todo: type these

  const drag = generateDragSpec({
    type: ELEMENT_LAYER_ITEM,
    item: {
      id: elemKey,
      index,
      layerType,
      type: element.get("type"),
      containedBy: groupId,
    },
    onDrop: ({ data }) => {
      DocumentStore.dispatch({ type: COMMANDS.MOVE_LAYER, payload: data });
    },
  });

  const drop = generateDropSpec({
    canDrop: (item, monitor) => {
      const type = element.get("type");
      const bothGroups = type === DataTypes.GROUP && item.type === DataTypes.GROUP;
      const targetArea = getTargetArea(monitor, selfRef.current, true);
      
      const isCenterGroupOnGroupDrop = bothGroups && targetArea === CENTER;
      const isBottomOpenDrop = isGroupOpen && targetArea === BOTTOM;

      // prevents group in group drops
      if (isCenterGroupOnGroupDrop || isBottomOpenDrop) return false;

      return groupId ? item.type !== DataTypes.GROUP : true;
    },
    type: ELEMENT_LAYER_ITEM,
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current, isGroupLayer)),
    onDrop: pagePanelDrop({
      dropRef: selfRef.current,
      id: elemKey,
      index,
      containedBy: groupId || (isGroupLayer ? element.get("id") : undefined),
      layerType: isGroupLayer ? ITEM_TYPE_GROUP : ITEM_TYPE_ELEMENT,
      allowCenterDrop: isGroupLayer,
      isGroupOpen,
    }),
  });

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ isOver, canDrop }, dropRef] = useDrop(drop);

  const _updateVisible = (newVis, e) => {
    e.stopPropagation();
    e.preventDefault();

    const visible = element.get("visible");

    onVisibleChange && onVisibleChange(updateKey, newVis, visible);
  };

  const _remove = () => {
    DocumentStore.dispatch({ type: COMMANDS.REMOVE_ELEMENT, payload: [updateKey] });
  };

  const _rename = () => {
    UIActions.toggleRenamePopup({
      title: "Element",
      itemType: ITEM_TYPE_ELEMENT,
      itemId: updateKey,
      name: element.get("name"),
      slug: element.get("slug"),
    });
  };

  const _duplicate = () => {
    DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_ELEMENT, payload: [updateKey] });
  };

  const _onMouseEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIconHover(true);

    UIActions.itemOver(IDUtils.getID(updateKey, selected.get("layout")));
  };

  const _onMouseLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIconHover(false);
    UIActions.itemOut();
  };

  const _toggleLayerLock = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // disable if controlled by parent
    if (locked) return;

    const lock = element.get(LOCK_EDITING_KEY);

    const update = new BlockUpdateVO();
    update.add(updateKey, LOCK_EDITING_KEY, lock === "1" ? null : "1");
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  const active = typeof element.get("selected") === "object";

  useEffect(() => {
    if (active) {
      setSelectedChildren((prevState) => ({ ...prevState, [elemKey]: true }));
      if (stateChild) {
        setSelectedCanvasView(elemKey);
        setIsCanvasViewOpened(true);
      }
    } else {
      setSelectedChildren((prevState) => {
        const { [elemKey]: _, ...rest } = prevState;
        return { ...rest };
      });
    }
  }, [
    active,
    selected,
    setSelectedChildren,
    elemKey,
    stateChild,
    setIsCanvasViewOpened,
    setSelectedCanvasView,
  ]);

  const visible = element.get("visible");
  const { iconClass, label } = getElementStyle(element, iconHover);
  const elementIsLocked = locked || element.get(LOCK_EDITING_KEY) === "1";

  const elementHeadingStyle = {};

  if (isOver && canDrop && !isDragging) {
    if (dropArea === DROP_AREA_CENTER) {
      elementHeadingStyle.backgroundColor = DROP_CONTAINER_BACKGROUND;
    }
    elementHeadingStyle.boxShadow =
      dropArea === DROP_AREA_TOP
        ? DROP_SHADOW_TOP
        : dropArea === DROP_AREA_BOTTOM
        ? DROP_SHADOW_BOTTOM
        : DROP_SHADOW_CENTER;
  }
  if (isDragging) elementHeadingStyle.opacity = 0.4;

  const _renderDeviceIcons = () => {
    const mobileActive = [VISIBLE_ALL, VISIBLE_MOBILE].includes(visible);
    const desktopActive = [VISIBLE_ALL, VISIBLE_DESKTOP].includes(visible);

    return (
      <div className={styles["device-icons"]}>
        <div
          className={cx(styles["mobile-device-icon"], {
            [styles["mobile-device-icon-off"]]: mobileActive,
          })}
        >
          <Icon
            type={mobileActive ? "mobileDevice" : "mobileOff"}
            size="20"
            onClick={(e) => _updateVisible(VISIBLE_MOBILE, e)}
          />
        </div>
        <div
          className={cx(styles["desktop-device-icon"], {
            [styles["desktop-device-icon-off"]]: desktopActive,
          })}
        >
          <Icon
            type={desktopActive ? "desktopDevice" : "desktopOff"}
            size="20"
            onClick={(e) => _updateVisible(VISIBLE_DESKTOP, e)}
          />
        </div>
      </div>
    );
  };

  const _multipleElementsSelected = useMemo(
    () => Object.values(selectedChildren).filter(Boolean).length > 1,
    [selectedChildren]
  );

  const isDistant =
    !active &&
    selectedCanvasView &&
    !stateChild &&
    (!_multipleElementsSelected || !nonCanvasViewElementSelected) &&
    !nonCanvasViewElementSelected &&
    true;

  const themeClass = cx(styles.root, {
    [styles.active]: active,
  });

  const _handleSelect = (e) => {
    if (!stateChild) {
      setSelectedCanvasView(null);
    }
    if (groupId) {
      e.stopPropagation();
      e.preventDefault();

      const layout = selected.get("layout");
      const blockId = selected.get("blockId");
      const { multi, range } = EventUtils.selectionType(e) || {};

      // Then select the group element
      UIActions.selectElement({
        id: elemKey,
        type: element.get("type"),
        layout,
        blockId,
        addTo: multi,
        addRange: range,
        from: "panel",
      });

      return; // Exit early to prevent onSelect from being called
    }
    if (stateChild) {
      setSelectedCanvasView(elemKey);
    }
    onSelect(e);
  };

  return (
    <div
      data-e2e={uiPrefix(`layer-type-${slugify(label)}`)}
      data-testid="layer-element"
      className={`layer-element ${themeClass}`}
      style={elementHeadingStyle}
      onClick={_handleSelect}
      onContextMenu={(e) =>
        openRowContextMenu({ event: e, rowEl: selfRef.current, active, select: _handleSelect })
      }
      onMouseEnter={_onMouseEnter}
      onMouseLeave={_onMouseLeave}
      ref={(self) => {
        selfRef.current = self;
        if (!elementIsLocked) {
          dragRef(self);
          dropRef(self);
        }
      }}
    >
      <div className={cx(styles.label, { [styles.child]: groupId })}>
        {_renderDeviceIcons()}
        <div className={styles["element-label-container"]}>
          <Icon type={iconClass} size="20" className={styles["element-icon"]} />
          <div className={`${styles.text} ${groupId ? styles.child : ""}`} onDoubleClick={_rename}>
            {label}
          </div>
        </div>
      </div>
      <div className={styles.menu}>
        <button
          type="button"
          onClick={_toggleLayerLock}
          className={cx(styles["lock-icon"], {
            [styles["lock-icon-active"]]: !elementIsLocked,
          })}
        >
          <Icon type={elementIsLocked ? "lock" : "unlock"} size="20" />
        </button>
        <div
          className={cx(styles["menu-container"], {
            [styles["menu-container-active"]]: !active,
          })}
        >
          <PagePanelElementContextMenu
            element={element}
            toggleLayerLock={_toggleLayerLock}
            remove={_remove}
            duplicate={_duplicate}
            rename={_rename}
            size={UI_BUTTON_SIZE_SMALL}
            theme={theme}
            state={isDistant ? PAGE_PANEL_STATE_DISTANT : PAGE_PANEL_STATE_DEFAULT}
            active={active}
          />
        </div>
      </div>
    </div>
  );
};

export default LayerPanelElement;
